import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface Memory {
  id: number
  name: string
  description: string
  content: string
  memory_type: string
  priority: number
  hook_keywords: string[]
  active: boolean
  injection_count: number
  search_count: number
  hook_count: number
  project_id: number
  project_name: string
  user_id: number | null
  user_email: string | null
  conversation_id: number | null
  message_id: number | null
  dev_instance_id: number | null
  dev_instance_name: string | null
  created_at: string
  updated_at: string
}

interface Props {
  memory: Memory
}

const PRIORITY_LABELS: Record<number, string> = { 1: 'Low', 2: 'Medium', 3: 'High' }

function Show({ memory }: Props) {
  const handleDeactivate = () => {
    if (window.confirm('Deactivate this memory?')) {
      router.post(`/admin/collective_knowledge/memories/${memory.id}/deactivate`)
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4">
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Link href="/admin/collective_knowledge/memories" className="text-sm text-muted-foreground hover:text-foreground">
                  ← Memories
                </Link>
                <h1 className="text-lg font-semibold">{memory.name}</h1>
                <Badge variant={memory.active ? 'success' : 'destructive'}>
                  {memory.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {memory.active && (
                <Button variant="destructive" size="sm" onClick={handleDeactivate}>
                  Deactivate
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 flex flex-col gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{memory.description}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Content</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-sm whitespace-pre-wrap font-mono bg-muted p-3 rounded-md">{memory.content}</pre>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-col gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Project</span>
                      <span>{memory.project_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type</span>
                      <Badge variant="secondary">{memory.memory_type}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Priority</span>
                      <span>{PRIORITY_LABELS[memory.priority]} ({memory.priority})</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Injections</span>
                      <span>{memory.injection_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Searches</span>
                      <span>{memory.search_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Hooks</span>
                      <span>{memory.hook_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Created</span>
                      <span>{new Date(memory.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Updated</span>
                      <span>{new Date(memory.updated_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>

                {memory.hook_keywords.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Hook Keywords</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1">
                        {memory.hook_keywords.map((kw) => (
                          <Badge key={kw} variant="outline" className="text-xs">{kw}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Source</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-xs text-muted-foreground">
                    {memory.dev_instance_id && <p>Instance: {memory.dev_instance_name || memory.dev_instance_id}</p>}
                    {memory.conversation_id && <p>Conversation: {memory.conversation_id}</p>}
                    {memory.message_id && <p>Message: {memory.message_id}</p>}
                    {memory.user_id && <p>User: {memory.user_email || memory.user_id}</p>}
                    {!memory.dev_instance_id && !memory.conversation_id && <p>No source tracking</p>}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
